import React, { useState } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { ScrollText, RefreshCw, Download, Search, ChevronLeft, ChevronRight, ChevronDown, ChevronUp, X } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { getAuditLog, getAuditFilters, exportAuditLog } from '../../config/api';

const fmt = (d) => (d ? new Date(d).toLocaleString() : '');

// Colour the action badge by the kind of change it represents.
const actionClass = (action = '') => {
  const a = action.toUpperCase();
  if (/(CREATE|APPROVE|ISSUE|RESTORE|MATCH|APPLY|IMPORT|UNARCHIVE)/.test(a)) return 'bg-green-100 text-green-800';
  if (/(DELETE|REJECT|VOID|REVERSE|PURGE|WRITEOFF|DISPUTE|ARCHIVE)/.test(a)) return 'bg-red-100 text-red-800';
  if (/(UPDATE|EDIT|RECONCILE|RESET|SUBMIT|COMMUNICATE)/.test(a)) return 'bg-blue-100 text-blue-800';
  return 'bg-gray-100 text-gray-800';
};

const PAGE_SIZE = 50;

const JsonBlock = ({ label, value }) => {
  if (value == null) return null;
  return (
    <div className="flex-1 min-w-0">
      <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">{label}</div>
      <pre className="text-xs bg-gray-50 border border-gray-200 rounded p-2 overflow-x-auto max-h-56 whitespace-pre-wrap break-words">
        {JSON.stringify(value, null, 2)}
      </pre>
    </div>
  );
};

const AuditLog = () => {
  const [page, setPage] = useState(1);
  const [expanded, setExpanded] = useState(null);
  // Applied filters (what the query uses).
  const [filters, setFilters] = useState({ search: '', userId: '', action: '', entityType: '', startDate: '', endDate: '' });
  // Draft search text (applied on Enter / button so we don't re-query every keystroke).
  const [searchDraft, setSearchDraft] = useState('');
  const [exporting, setExporting] = useState(false);

  const { data: filterOpts } = useQuery({
    queryKey: ['audit-filters'],
    queryFn: getAuditFilters,
    staleTime: 60_000
  });

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ['audit-log', page, filters],
    queryFn: () => getAuditLog({ page, limit: PAGE_SIZE, ...filters }),
    placeholderData: keepPreviousData
  });

  const logs = data?.logs || [];
  const pagination = data?.pagination || { page: 1, pages: 1, total: 0 };

  const setFilter = (key, value) => { setFilters((f) => ({ ...f, [key]: value })); setPage(1); };
  const applySearch = () => { setFilters((f) => ({ ...f, search: searchDraft.trim() })); setPage(1); };
  const clearFilters = () => {
    setFilters({ search: '', userId: '', action: '', entityType: '', startDate: '', endDate: '' });
    setSearchDraft(''); setPage(1);
  };
  const hasFilters = Object.values(filters).some((v) => v);

  const handleExport = async () => {
    setExporting(true);
    try { await exportAuditLog(filters); toast.success('Audit log exported'); }
    catch (e) { toast.error(e.response?.data?.error || 'Export failed'); }
    finally { setExporting(false); }
  };

  const userLabel = (u) => {
    if (!u) return <span className="text-gray-400 italic">System</span>;
    return (
      <span title={u.email || u.id}>
        {u.name || u.email || <span className="text-gray-400 italic">Unknown user</span>}
      </span>
    );
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-white rounded-lg shadow p-4 flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center">
          <ScrollText className="h-6 w-6 text-primary-600 mr-3" />
          <div>
            <h2 className="text-xl font-bold text-gray-900">Audit Log</h2>
            <p className="text-sm text-gray-500">Every state-changing action, who performed it, and when.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => refetch()}
            className="inline-flex items-center px-3 py-2 text-sm bg-white border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            title="Refresh"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${isFetching ? 'animate-spin' : ''}`} /> Refresh
          </button>
          <button
            onClick={handleExport}
            disabled={exporting}
            className="inline-flex items-center px-3 py-2 text-sm bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-60"
            title="Export the filtered audit log to CSV"
          >
            <Download className="h-4 w-4 mr-2" /> {exporting ? 'Exporting…' : 'Export CSV'}
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="lg:col-span-2">
            <label className="block text-xs font-medium text-gray-500 mb-1">Search (action, entity, id)</label>
            <div className="flex">
              <input
                value={searchDraft}
                onChange={(e) => setSearchDraft(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && applySearch()}
                placeholder="e.g. APPROVE, Contract, an id…"
                className="flex-1 border border-gray-300 rounded-l-md px-3 py-2 text-sm focus:ring-primary-500 focus:border-primary-500"
              />
              <button onClick={applySearch} className="px-3 bg-primary-600 text-white rounded-r-md hover:bg-primary-700" title="Search">
                <Search className="h-4 w-4" />
              </button>
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">User</label>
            <select value={filters.userId} onChange={(e) => setFilter('userId', e.target.value)} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm">
              <option value="">All users</option>
              {(filterOpts?.users || []).map((u) => (
                <option key={u.id} value={u.id}>{u.name || u.email}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Action</label>
            <select value={filters.action} onChange={(e) => setFilter('action', e.target.value)} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm">
              <option value="">All actions</option>
              {(filterOpts?.actions || []).map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Entity type</label>
            <select value={filters.entityType} onChange={(e) => setFilter('entityType', e.target.value)} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm">
              <option value="">All entities</option>
              {(filterOpts?.entityTypes || []).map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">From date</label>
            <input type="date" value={filters.startDate} onChange={(e) => setFilter('startDate', e.target.value)} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">To date</label>
            <input type="date" value={filters.endDate} onChange={(e) => setFilter('endDate', e.target.value)} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" />
          </div>
          <div className="flex items-end">
            {hasFilters && (
              <button onClick={clearFilters} className="inline-flex items-center px-3 py-2 text-sm text-gray-600 hover:text-gray-900" title="Clear all filters">
                <X className="h-4 w-4 mr-1" /> Clear filters
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden no-card-hover">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">When</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">User</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Action</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Entity</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Entity ID</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">IP</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Details</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {isLoading ? (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-gray-400">Loading…</td></tr>
              ) : logs.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-gray-400">No audit entries match your filters.</td></tr>
              ) : logs.map((log) => {
                const hasDetail = log.oldValues != null || log.newValues != null;
                const isOpen = expanded === log.id;
                return (
                  <React.Fragment key={log.id}>
                    <tr className="hover:bg-gray-50">
                      <td className="px-4 py-3 whitespace-nowrap text-gray-700 tabular-nums">{fmt(log.timestamp)}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-gray-900">{userLabel(log.user)}</td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${actionClass(log.action)}`}>{log.action}</span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-gray-700">{log.entityType}</td>
                      <td className="px-4 py-3 whitespace-nowrap font-mono text-xs text-gray-500" title={log.entityId}>
                        {log.entityId?.length > 14 ? `${log.entityId.slice(0, 14)}…` : log.entityId}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-gray-400 text-xs">{log.ipAddress || '—'}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-right">
                        {hasDetail ? (
                          <button
                            onClick={() => setExpanded(isOpen ? null : log.id)}
                            className="inline-flex items-center text-primary-600 hover:text-primary-800 text-xs font-medium"
                          >
                            {isOpen ? <>Hide <ChevronUp className="h-3 w-3 ml-1" /></> : <>View <ChevronDown className="h-3 w-3 ml-1" /></>}
                          </button>
                        ) : <span className="text-gray-300">—</span>}
                      </td>
                    </tr>
                    {isOpen && hasDetail && (
                      <tr className="bg-gray-50/60">
                        <td colSpan={7} className="px-4 py-3">
                          <div className="flex flex-col md:flex-row gap-4">
                            <JsonBlock label="Before" value={log.oldValues} />
                            <JsonBlock label="After" value={log.newValues} />
                          </div>
                          {log.userAgent && <div className="mt-2 text-xs text-gray-400 truncate" title={log.userAgent}>User agent: {log.userAgent}</div>}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-white">
          <div className="text-sm text-gray-500">
            {pagination.total > 0
              ? <>Showing page <span className="font-medium">{pagination.page}</span> of <span className="font-medium">{pagination.pages}</span> · <span className="font-medium">{pagination.total}</span> total</>
              : 'No entries'}
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={pagination.page <= 1}
              className="inline-flex items-center px-2 py-1.5 text-sm border border-gray-300 rounded-md text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="h-4 w-4" /> Prev
            </button>
            <button
              onClick={() => setPage((p) => Math.min(pagination.pages, p + 1))}
              disabled={pagination.page >= pagination.pages}
              className="inline-flex items-center px-2 py-1.5 text-sm border border-gray-300 rounded-md text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Next <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuditLog;
